import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export default function CmsEditor() {
  const [cms, setCms] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/cms").then(({ data }) => setCms({ features: [], ...data })).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!cms) return <p className="text-neutral-400">Loading…</p>;

  const set = (k) => (e) => setCms({ ...cms, [k]: e.target.value });
  const setFeature = (i, k, v) => {
    const features = cms.features.map((f, idx) => (idx === i ? { ...f, [k]: v } : f));
    setCms({ ...cms, features });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/cms", cms);
      toast.success("Homepage updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="cms-editor-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Homepage CMS</h1>
          <p className="text-sm text-neutral-500 mt-1">Edit the public marketing homepage content.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="text-sm underline text-neutral-500" data-testid="cms-preview-link">Preview homepage</a>
      </div>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-5">
        <div className="space-y-2">
          <Label>Badge text</Label>
          <Input value={cms.badge || ""} onChange={set("badge")} data-testid="cms-badge-input" />
        </div>
        <div className="space-y-2">
          <Label>Hero title</Label>
          <Textarea rows={2} value={cms.hero_title || ""} onChange={set("hero_title")} data-testid="cms-hero-title-input" />
        </div>
        <div className="space-y-2">
          <Label>Hero subtitle</Label>
          <Textarea rows={2} value={cms.hero_subtitle || ""} onChange={set("hero_subtitle")} data-testid="cms-hero-subtitle-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Primary CTA</Label>
            <Input value={cms.cta_primary || ""} onChange={set("cta_primary")} data-testid="cms-cta-primary-input" />
          </div>
          <div className="space-y-2">
            <Label>Secondary CTA</Label>
            <Input value={cms.cta_secondary || ""} onChange={set("cta_secondary")} data-testid="cms-cta-secondary-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Stat (e.g. 500+)</Label>
            <Input value={cms.stat_customers || ""} onChange={set("stat_customers")} data-testid="cms-stat-input" />
          </div>
          <div className="space-y-2">
            <Label>Stat label</Label>
            <Input value={cms.stat_label || ""} onChange={set("stat_label")} data-testid="cms-stat-label-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Footer text</Label>
          <Input value={cms.footer_text || ""} onChange={set("footer_text")} data-testid="cms-footer-input" />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-5">
        <div>
          <h2 className="font-bold">SEO</h2>
          <p className="text-xs text-neutral-400 mt-1">Controls the browser tab title and search engine meta tags.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Site name</Label>
            <Input value={cms.site_name || ""} onChange={set("site_name")} placeholder="CreateForms.app" data-testid="cms-site-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Page title (SEO)</Label>
            <Input value={cms.seo_title || ""} onChange={set("seo_title")} placeholder="CreateForms.app — Customer Journey Management" data-testid="cms-seo-title-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Meta description</Label>
          <Textarea rows={2} value={cms.seo_description || ""} onChange={set("seo_description")} data-testid="cms-seo-description-input" />
        </div>
        <div className="space-y-2">
          <Label>Meta keywords (comma separated)</Label>
          <Input value={cms.seo_keywords || ""} onChange={set("seo_keywords")} data-testid="cms-seo-keywords-input" />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-bold">Feature cards</h2>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setCms({ ...cms, features: [...cms.features, { title: "New feature", description: "" }] })}
            data-testid="cms-add-feature-button"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="mt-4 space-y-4">
          {cms.features.map((f, i) => (
            <div key={i} className="border border-[#E5E5E5] rounded-xl p-4 space-y-3" data-testid={`cms-feature-${i}`}>
              <div className="flex items-center gap-3">
                <Input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} placeholder="Feature title" data-testid={`cms-feature-title-${i}`} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 shrink-0"
                  onClick={() => setCms({ ...cms, features: cms.features.filter((_, idx) => idx !== i) })}
                  data-testid={`cms-feature-delete-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea rows={2} value={f.description} onChange={(e) => setFeature(i, "description", e.target.value)} placeholder="Description" data-testid={`cms-feature-description-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="mt-6 rounded-full px-8" data-testid="cms-save-button">
        {saving ? "Saving…" : "Publish changes"}
      </Button>
    </div>
  );
}
